import { FC } from "react";
import { useViewListContext } from "@/context/ViewListContext";
import { AlternatingMessages, Button, LoaderBar, useGoBack } from "@vixi/ui";
import { useCourseFromEbook } from "@/hooks/api/course/useGenerateCourse";
import { useTranslation } from "@vixi/localization";
import { DevOnly } from "@/components/DevOnly";
import { Img } from "@vixi/ui";

// Assets
import CourseGeneratedBubbleEn from "@assets/bubbles/course-generated.png";

// Constants
const CourseGeneratedBubble = {
  en: CourseGeneratedBubbleEn,
} as const;

interface CourseGenerationStepProps {
  uploadMutation: Omit<ReturnType<typeof useCourseFromEbook>, "mutate">;
  retry: () => void;
}

export const CourseGenerationStep: FC<CourseGenerationStepProps> = ({ retry, uploadMutation }) => {
  useGoBack(null);
  const { t, tFile, byLocale } = useTranslation();

  const mainFlow = useViewListContext();

  // State
  const { uploadProgress, isPending, isError, isSuccess, error } = uploadMutation;
  const isUploaded = uploadProgress === 100;

  if (isError && import.meta.env.DEV) {
    debugger;
  }

  if (!isSuccess) {
    return (
      <div className="w-105 text-center">
        <Img contain src={tFile("/images", "bubble-generating-course.png")} className="w-105" />

        {isError && <p className="text-error">{t("ErrorMessage.SomethingWentWrong")}</p>}

        <LoaderBar className="mx-auto my-2.5" error={isError} width={400} />

        {!isError && (isPending || isSuccess) && (
          <p>
            {!isUploaded ? (
              t("LoadingMessage.UploadingFile")
            ) : (
              <AlternatingMessages
                messages={[
                  "LoadingMessage.GrabSnack",
                  "LoadingMessage.CourseInMaking",
                  "LoadingMessage.Stretch",
                ]}
              />
            )}
          </p>
        )}

        {/* Show error message in development */}
        <DevOnly>
          <p className="text-error mb-2.5">{error?.message}</p>
        </DevOnly>

        {isError && (
          <Button variant="contained" size="large" onClick={retry} className="mt-0.5">
            {t("Action.TryAgain")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <Img
        src={byLocale({
          ...CourseGeneratedBubble,
          fallback: tFile("/images", "bubble-congrats.png"),
        })}
        className="w-110"
      />
      <Button
        variant="contained"
        size="large"
        type="submit"
        onClick={mainFlow?.nextStep}
        className="absolute end-20 bottom-12.5"
      >
        {t("Action.Next")}
      </Button>
    </>
  );
};
